<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Lotes | Gerenciar" :on-back="() => router.go(-1)" />

    <v-card class="mb-6">
      <v-row class="pa-8">
        <v-col>
          <v-select
            v-model="events.model"
            variant="outlined"
            label="Evento"
            :items="events.items"
            hide-details
            dense
            color="orange lighten-2"
            density="compact"
            :disabled="loader.loading"
          />
        </v-col>
      </v-row>
      <v-progress-linear v-show="loader.loading" indeterminate color="primary" />
    </v-card>

    <template v-if="gridLoaded">
      <div :class="{ 'grid-stale': switching > 0 }">
        <batch-status-grid
          :ticket-types="ticketTypes"
          :pos-list="posList"
          :event="events.resource!"
          :batch-grid="batchGrid"
          :selected-channel="selectedChannel"
          @select-channel="selectedChannel = $event"
        />
      </div>

      <batch-channel-editor
        v-if="selectedChannel"
        :channel="selectedChannel"
        :event="events.resource!"
        :batch-grid="batchGrid"
        :ticket-types="ticketTypes"
        :pos-list="posList"
        :event-id="events.model!"
        @saved="onSaved"
        @request-delete="destroy"
        @close="selectedChannel = null"
      />
      <v-card v-else class="rounded-t-0 pa-8" style="border-top: 2px solid rgb(var(--v-theme-primary));">
        <div class="text-center text-body-2 text-medium-emphasis">
          Selecione um canal na grade acima para gerenciar seus lotes.
        </div>
      </v-card>
    </template>
  </section>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { DateTime, Interval } from 'luxon'
import BatchChannelEditor from './partials/batch-channel-editor.vue'
import BatchStatusGrid from './partials/batch-status-grid.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import sortPos from '@/helpers/sorting/pos'
import EventConfigurationService from '@/services/EventConfigurationService'
import PointOfSaleService from '@/services/PointOfSaleService'
import TicketBatchService from '@/services/TicketBatchService'
import { useDialog } from '@/stores/dialog'
import { useLoader } from '@/stores/loader'
import { useProducerStore } from '@/stores/producer'
import { useSnackBar } from '@/stores/snackbar'
import type PointOfSale from '@/types/PointOfSale'
import type TicketBatch from '@/types/TicketBatch'
import type TicketType from '@/types/TicketType'
import axios from 'axios'

defineOptions({ name: 'ManageScheduledBatches' })

const router = useRouter()
const loader = useLoader()
const snackbar = useSnackBar()
const dialog_store = useDialog()
const service = new TicketBatchService()

const producer = ref(useProducerStore().producer!)

// --- State ---

/**
 * `Record<type_id, Record<pos_id, TicketBatch[]>>`
 */
type BatchGrid = Record<string, Record<string, TicketBatch[]>>

const ticketTypes = ref<TicketType[]>([])
const posList = ref<PointOfSale[]>([])
const gridLoaded = ref<boolean>(false)
const batchGrid = ref<BatchGrid>({})
const switching = ref<number>(0)

interface Channel {
  type: TicketType
  pos: PointOfSale
}
const selectedChannel = ref<Channel | null>(null)

// --- Events ---

const events = ref(useEventFilter({
  producer: producer.value,
  label: 'Evento',
  field: 'event_id',
  type: 'select',
  queryParams: {
    end_between: Interval.fromDateTimes(
      DateTime.now(),
      DateTime.now().plus({ years: 5 }),
    ).toISO(),
  },
}))

events.value.ready.then(() => {
  if (events.value.items?.length === 0) {
    snackbar.show('Não há eventos futuros disponíveis no momento.', 'info')
  }
})

// --- Data loading ---

function clearGrid(): void {
  gridLoaded.value = false
  ticketTypes.value = []
  posList.value = []
  batchGrid.value = {}
}

watch(
  () => events.value.resource,
  async (event) => {
    selectedChannel.value = null

    if (!event?.configuration?.id) {
      clearGrid()

      return
    }

    const requested = event.id

    try {
      loader.increment()
      switching.value++

      const [configResponse, posResponse] = await Promise.all([
        new EventConfigurationService().show(event.configuration.id),
        new PointOfSaleService().index(1, 'active=true', 100),
      ])

      const mapping = configResponse.data.data?.mapping

      const types = (mapping?.sectors ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .flatMap(s => s.sections ?? [])
        .flatMap(s => (s.ticket_types ?? []).slice().sort((a, b) => a.sorting_order - b.sorting_order))

      const pos = sortPos(posResponse.data.data ?? [])
      const grid = await loadGrid(requested, types, pos)

      if (events.value.resource?.id !== requested) return

      ticketTypes.value = types
      posList.value = pos
      batchGrid.value = grid
      gridLoaded.value = true
    } catch {
      clearGrid()

      snackbar.show('Não foi possível carregar os canais deste evento.', 'error')
    } finally {
      loader.decrement()
      switching.value--
    }
  },
)

async function loadGrid(eventId: string, types: TicketType[], pos: PointOfSale[]): Promise<BatchGrid> {
  const response = await service.index(eventId)

  const batches: TicketBatch[] = response.data.data ?? []

  const grid: BatchGrid = {}

  for (const type of types) {
    grid[type.id] = {}
    for (const p of pos) {
      grid[type.id][p.id] = []
    }
  }

  for (const batch of batches) {
    if (batch.ticket_type_id && batch.pos_id) {
      grid[batch.ticket_type_id] ??= {}
      grid[batch.ticket_type_id][batch.pos_id] ??= []
      grid[batch.ticket_type_id][batch.pos_id].push(batch)
    }
  }

  return grid
}

async function fetchBatches(): Promise<void> {
  const eventId = events.value.model

  if (!eventId) return

  try {
    batchGrid.value = await loadGrid(eventId, ticketTypes.value, posList.value)
  } catch {
    batchGrid.value = {}

    snackbar.show('Não foi possível carregar os lotes.', 'error')
  }
}

async function onSaved(): Promise<void> {
  await fetchBatches()
}

async function destroy(batch: TicketBatch): Promise<void> {
  const confirmed = await dialog_store.open({
    title: 'Excluir Lote',
    message: `Deseja excluir o Lote ${batch.number}? Esta ação é irreversível.`,
  })

  const eventId = events.value.model

  if (!confirmed || !selectedChannel.value || !eventId) return

  try {
    loader.increment()

    await service.destroy(eventId, batch.id)

    snackbar.show('Lote excluído com sucesso.', 'success')

    await fetchBatches()
  } catch (error) {
    if (axios.isAxiosError(error)) {
      snackbar.fromAxiosResponse(error.response?.data)
    } else {
      snackbar.show('Erro ao excluir lote.', 'error')
    }
  } finally {
    loader.decrement()
  }
}
</script>

<style scoped>
.grid-stale {
  opacity: 0.4;
  pointer-events: none;
  transition: opacity 150ms ease;
}
</style>
